import CardContainer from "@/components/Card/CardContainer";
import CustomLinearProgress from "@/components/Progress/CustomLinearProgress";
import TooltipWrapper from "@/components/Tooltip/CustomTooltipWrapper";
import { FONT_FAMILY } from "@/constants";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Typography } from "@mui/material";
import QuotaTooltipContent from "./QuotaTooltipContent";

interface QuotaUsageCardProps {
  title: string;
  usage: number;
  total: number;
  limit: number | string;
  rollover: number;
  isUnlimited: boolean;
}

export default function QuotaUsageCard({
  title,
  usage,
  total,
  limit,
  rollover,
  isUnlimited,
}: QuotaUsageCardProps) {
  const percentageUsed =
    !isUnlimited && total > 0 ? Math.min((usage / total) * 100, 100) : 0;
  const isHighUsage = percentageUsed > 80;

  const formatNumber = (num: number) => num.toLocaleString();

  return (
    <CardContainer
      sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body1Semi">{title}</Typography>
        <TooltipWrapper
          title={
            <QuotaTooltipContent
              monthlyLimit={limit}
              rollover={rollover}
              usage={usage}
              isUnlimited={isUnlimited}
            />
          }
          placement="top"
        >
          <InfoOutlinedIcon
            fontSize="small"
            sx={{ color: "text.secondary", cursor: "pointer" }}
          />
        </TooltipWrapper>
      </Box>

      <Box>
        <Typography
          variant="h4"
          component="span"
          sx={{ fontWeight: 600, fontSize: "1.6rem", fontFamily: FONT_FAMILY }}
        >
          {formatNumber(usage)}
        </Typography>
        <Typography
          variant="body1Reg"
          component="span"
          sx={{ color: "text.secondary", ml: 0.5 }}
        >
          / {isUnlimited ? "∞" : formatNumber(total)}
        </Typography>
      </Box>

      {!isUnlimited && (
        <Box>
          <CustomLinearProgress
            value={percentageUsed}
            sx={{
              "& .MuiLinearProgress-bar": {
                backgroundColor: isHighUsage ? "error.main" : "primary.main",
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 0.5,
            }}
          >
            <Typography variant="captionReg" color="text.secondary">
              {rollover > 0 && `+${formatNumber(rollover)} rollover`}
            </Typography>
            <Typography variant="captionReg" color="text.secondary">
              {percentageUsed.toFixed(1)}% used
            </Typography>
          </Box>
        </Box>
      )}
    </CardContainer>
  );
}
